import {IPopupable} from "back/commons/widgets/popupable";
import {POPUP} from "back/commons/widgets/popups";
import {Tabs} from "back/commons/widgets/tabs";
import {AgBoxSelEditor, IWedEditorBoxSel} from "back/edit/wed/features/boxSel";
import {AgCommonBarEditor, IWedEditorCommonBar, OWedEditorCommonBarConfig} from "back/edit/wed/features/commonBar";
import {AgTxtSelEditor} from "back/edit/wed/features/txtSel";
import {IWedletModel} from "back/edit/wed/wedCore";
import {IWedEdBar, IWedEditor, IWedEditorMain, OWedEditorConfig, OWedEditorMainConfig, OWedManagerConfig, WedEditorBase, WedMgr} from "back/edit/wed/wedEditor";
import {OWedEditorBoxConfig, WedEditorBox} from "back/edit/wed/wedEditorBox";
import {IWedlet, WEDLET} from "back/edit/wed/wedlets/wedlet";
import {Action, IActionToggle} from "lib/commons/actions";
import {Area} from "lib/commons/areas";
import {VIEWS} from "lib/commons/views";
import {DOM, JSX} from "lib/commons/xml/dom";
import {IDocHolderSync} from "lib/edit/docHolder";
import {OButtonInit} from "back/commons/widgets/buttons";
import {IWedEditorSearchBar} from "back/edit/wed/features/searchBar";
import {IWedEditorDiffBar, OWedEditorDiffBarConfig} from "back/edit/wed/features/diffBar";

/** Config de l'edditor dédié à la gestion des metas. */
export interface OWedEditorMetaConfig extends OWedEditorMainConfig {
	/**
	 * Affichage de la barre des métas :
	 * - none : pas de barre de metas disponible.
	 * - available : barre de métas dispo mais masquée à l'init, affichable à la demande.
	 * - fixed : barre de métas dispo et affichée en permanence.
	 */
	metaBarMode?: 'none' | 'shown' | 'available'
}

/** Enrichissements de l'éditor principal pour gérer l'editor de metas. */
export interface IWedEditorMetaBar extends IWedEditorMain {
	metaBar?: MetaBar
	metaBarMode?: 'none' | 'shown' | 'available'

	showMetaBar?(show: boolean): MetaBar
}

export function AgMetaBarEditor(cls: any): any {
	const proto = cls.prototype as IWedEditorMetaBar;

	const superInit = proto.initialize;
	proto.initialize = function (this: IWedEditorMetaBar, init: OWedEditorMetaConfig) {
		superInit.call(this, init);
		this.metaBarMode = init.metaBarMode || 'none';
		if (this.metaBarMode === 'shown') this.showMetaBar(true);
		return this;
	};

	proto.showMetaBar = function (this: IWedEditorMetaBar & IWedEditorCommonBar, show: boolean): MetaBar {
		if (this.metaBarMode === 'none') return null;
		if (show) {
			if (!this.metaBar) {
				const init = Object.create(this.config) as OWedEditorCommonBarConfig;
				if (this.commonBar) {
					init.commonBar = this.commonBar;
				} else {
					init.hideCommonBar = true;
				}
				init.lastDatasKey = null;
				init.lastDatas = null; // à creuser : mémoire de la meta en cours d'édition ?
				this.metaBar = new MetaBar().initialize(init).initMetaBar(this);
			} else this.metaBar.hidden = false;
			this.barLayout.showBar(this.metaBar)
		} else if (this.metaBar) {
			if (DOM.setHidden(this.metaBar, true)) this.barLayout.onHideBar(this.metaBar);
		}
		return this.metaBar;
	};

	return cls;
}

/** FIXME wed-meta-bar doit être un customElement parent de WedEditorBase pour injecter des onglets contenant un WedEditorBase en cas de multi-metas. */
class MetaBar extends WedEditorBase implements CustomElement, IWedEdBar {
	mainEditor: IWedEditorMetaBar;

	/** Références des métas en cours d'édition. */
	currentMetaDefs: IMetaDef[];

	constructor() {
		super();
		this.id = "metaBar";
		this.setAttribute("label", "Propriétés");
	}

	initMetaBar(mainEditor: IWedEditorMetaBar): this {
		this.mainEditor = mainEditor;
		const mainWedMgr = this.mainEditor.wedMgr;
		const clear = () => {this.wedMgr.clearEditor()};
		mainWedMgr.hookFct("clearEditor", clear);
		mainWedMgr.listeners.on("redrawAtEnd", clear);
		const detach = () => {this.wedMgr.detachDocHolder()};
		mainWedMgr.hookFct("detachDocHolder", detach);
		this.addEventListener("wed-editor-root-deleted", detach);
		this.style.flex = "1";
		this.style.backgroundColor = "var(--edit-bgcolor)";
		this.style.color = "var(--edit-color)";
		return this;
	}

	isAlreadyEditing(metaDefs: IMetaDef | IMetaDef[]): boolean {
		if (!this.currentMetaDefs) return false;
		return this.currentMetaDefs[0].from === (Array.isArray(metaDefs) ? metaDefs[0] : metaDefs).from;
	}

	editMeta(metaDefs: IMetaDef | IMetaDef[]) {
		if (Array.isArray(metaDefs) && metaDefs.length > 0) {
			throw "TODO edit metas multi";
		}
		const firstDef = Array.isArray(metaDefs) ? metaDefs[0] : metaDefs;
		this.setAttribute("label", "Propriétés de : " + firstDef.label);
		VIEWS.dispatchViewChange(this);
		const mainWedMgr = this.mainEditor.wedMgr;
		this.wedMgr.detachDocHolder();
		this.setWedModel(mainWedMgr.wedModel);
		const newDocHolder = mainWedMgr.docHolderAsync.cloneDocHolder();
		const wedMgrConfig = Object.create(mainWedMgr.config) as OWedManagerConfig;
		wedMgrConfig.readOnlyCauses = Array.from(mainWedMgr.readOnlyCauses);
		wedMgrConfig.xaRoot = firstDef.from.wedAnchor;
		wedMgrConfig.rootModel = firstDef.model;
		if (!WEDLET.isWritableWedlet(firstDef.from)) wedMgrConfig.readOnlyCauses = ["wedletOwner"];
		//wedMgrConfig.undoRedoFilter = newDocHolder.isMsgFromUs.bind(newDocHolder);
		this.initFromDocHolder(newDocHolder, wedMgrConfig);
	}

}

AgBoxSelEditor(AgTxtSelEditor(AgCommonBarEditor(MetaBar)));


customElements.define("wed-meta-bar", MetaBar);

/** Activation de la panel/bar des metas*/
export class ShowMetaBarAction extends Action<IWedEditor & IWedEditorMetaBar> implements IActionToggle<IWedEditor & IWedEditorMetaBar> {

	constructor() {
		super("metaBar");
		this._label = "Propriétés";
	}

	isVisible(ctx: IWedEditor & IWedEditorMetaBar) {
		return ctx.metaBarMode && ctx.metaBarMode !== 'none';
	}

	isToggle(): this is IActionToggle<IWedEditor & IWedEditorMetaBar> {return true}

	getDatas(api: 'toggle', ctx: IWedEditor & IWedEditorMetaBar): boolean | null {
		return ctx.metaBar != null && !ctx.metaBar.hidden;
	}

	execute(ctx: IWedEditor & IWedEditorMetaBar, ev?: Event): any | 'noPreventDefault' {
		ctx.showMetaBar(!ctx.metaBar || ctx.metaBar.hidden);
	}
}

export interface IMetaDef {
	label: string
	//TODO icon?: string
	//TODO desc?:string
	from: IWedlet,
	model: IWedletModel
}

export namespace METAS {

	/**
	 * Affiche les metas dans un panelBar de l'editeur, ou à défaut en popupDialog.
	 * Retourne une Promise si ouverture d'un dialogue, null si affichage dans un panel.
	 * TODO pour ce mode avec la barre affichée en permanence, implémenter la synchro avec la sel courante à partir de txtSel
	 */
	export function showMeta(metaDefs: IMetaDef | IMetaDef[]): Promise<void> | null {
		const firstDef = Array.isArray(metaDefs) ? metaDefs[0] : metaDefs;
		const fromWedMgr = firstDef.from.wedMgr;
		const fromEditor = fromWedMgr.wedEditor as IWedEditorMetaBar;
		if (fromEditor.metaBarMode && fromEditor.metaBarMode !== "none") {
			let metaBar = fromEditor.metaBar;
			if (metaBar && metaBar.isAlreadyEditing(metaDefs)) return null; //déjà en cours
			metaBar = fromEditor.showMetaBar(true);
			metaBar.editMeta(metaDefs);
			return null;
		}
		//Ouverture en popup dialog...
		return openMeta(metaDefs);
	}

	/**
	 * Affiche les metas dans un popup dialog.
	 * Retourne une Promise à la fermeture du dialogue.
	 */
	export function openMeta(metaDefs: IMetaDef | IMetaDef[]): Promise<void> {
		const firstDef = Array.isArray(metaDefs) ? metaDefs[0] : metaDefs;
		const fromWedMgr = firstDef.from.wedMgr;
		let popup: IPopupable<void>;
		if (Array.isArray(metaDefs) && metaDefs.length > 0) {
			const tabs = new Tabs().initialize({
				areas: metaDefs.map((m: IMetaDef) => new MetaEditorTab(m)),
				areasContext: null,
				skinOver: "webzone:panel"
			});
			tabs.style.margin = ".5em";
			tabs.style.flex = "1";
			popup = POPUP.showDialog(tabs, fromWedMgr.wedEditor.rootNode, {
				titleBar: {barLabel: {label: "Propriétés"}, closeButton: dialogMetasCloseBtn},
				resizer: {},
				initWidth: '80vw',
				initHeight: '60vh',
				closeOnCtrlEnter: true
			});
		} else {
			const editor = new MetaEditorTab(firstDef).buildBody();
			editor.reg.installSkin("webzone:panel", editor.shadowRoot);
			//Focus le 1er wed focusable
			editor.wedMgr.listeners.once("redrawAtEnd", (wedMgr: WedMgr) => {
				(wedMgr.wedEditor as IWedEditorBoxSel).boxSelMgr.focusFirst(wedMgr.rootWedlet);
			});
			popup = POPUP.showDialog(editor, fromWedMgr.wedEditor.rootNode, {
				titleBar: {barLabel: {label: "Propriétés : " + firstDef.label}, closeButton: dialogMetasCloseBtn},
				resizer: {},
				initWidth: '80vw',
				initHeight: '60vh',
				closeOnCtrlEnter: true
			});
		}
		popup.addEventListener('wed-editor-root-deleted', () => {
			POPUP.showNotifInfo("L'élément portant ces propriétés a été supprimé.", fromWedMgr.wedEditor.rootNode);
			popup.close();
		});
		return popup.onNextClose();
	}

	/** Edite des métas dans une house locale avant insertion dans le l'item lors de la création d'une balise. */
	export async function openMetaNew(wedMgr: WedMgr, docHolder: IDocHolderSync, metaModel: IWedletModel, title: string, parent: IWedlet): Promise<void> {
		const wedMgrConfig = Object.create(wedMgr.config) as OWedManagerConfig;
		wedMgrConfig.readOnlyCauses = Array.from(wedMgr.readOnlyCauses);
		wedMgrConfig.xaRoot = [0];
		wedMgrConfig.rootModel = metaModel;
		if (parent && !WEDLET.isWritableWedlet(parent)) wedMgrConfig.readOnlyCauses = ["wedletOwner"];
		const editorConfig = Object.create(wedMgr.wedEditor.config) as OWedEditorConfig & OWedEditorDiffBarConfig;
		editorConfig.hideDiffBar = true; //pas de diff pour metas en création
		const newEditor = new WedEditorBox().initialize(editorConfig) as WedEditorBox;
		newEditor.reg.installSkin("webzone:panel", newEditor.shadowRoot);
		newEditor.wedModel = wedMgr.wedModel;
		newEditor.initFromDocHolder(docHolder, wedMgrConfig);
		//Focus le 1er wed focusable
		newEditor.wedMgr.listeners.once("redrawAtEnd", (wedMgr: WedMgr) => {
			(wedMgr.wedEditor as IWedEditorBoxSel).boxSelMgr.focusFirst(wedMgr.rootWedlet);
		});
		await POPUP.showDialog(newEditor, wedMgr.wedEditor.rootNode, {
			titleBar: {barLabel: {label: title}, closeButton: dialogMetasCloseBtn},
			resizer: {},
			initWidth: '80vw',
			initHeight: '60vh',
			closeOnCtrlEnter: true
		}).onNextClose();
	}

	const dialogMetasCloseBtn: OButtonInit = {uiContext: 'dialog', label: "Fermer", title: "Fermer l'édition de ces propriétés (vos modifications sont préservées)"};
}

/** Area construisant l'editor d'un meta. */
class MetaEditorTab extends Area<null, WedEditorBox> {

	constructor(public metaDef: IMetaDef) {
		super();
	}

	getLabel() {return this.metaDef.label}

	buildBody(): WedEditorBox {
		const fromWedMgr = this.metaDef.from.wedMgr;
		const editorConfig = Object.create(fromWedMgr.wedEditor.config) as OWedEditorBoxConfig;
		editorConfig.disableVirtuals = fromWedMgr.reg.getPref("wed.metaBar.disableVirtuals", false);
		const editor = new WedEditorBox().initialize(editorConfig) as WedEditorBox & IWedEditorSearchBar & IWedEditorDiffBar;
		editor.wedModel = fromWedMgr.wedModel;
		const newDocHolder = fromWedMgr.docHolderAsync.cloneDocHolder();
		const wedMgrConfig = Object.create(fromWedMgr.config) as OWedManagerConfig;
		wedMgrConfig.readOnlyCauses = Array.from(fromWedMgr.readOnlyCauses);
		wedMgrConfig.xaRoot = this.metaDef.from.wedAnchor;
		wedMgrConfig.rootModel = this.metaDef.model;
		if (!WEDLET.isWritableWedlet(this.metaDef.from)) wedMgrConfig.readOnlyCauses = ["wedletOwner"];
		wedMgrConfig.undoRedoFilter = newDocHolder.isMsgFromUs.bind(newDocHolder);
		editor.initFromDocHolder(newDocHolder, wedMgrConfig);
		//transfert searchBar.
		const searchP = (fromWedMgr.wedEditor as IWedEditorSearchBar).searchBar?.getSearchParams();
		if (searchP) editor.searchBar?.setSearch(searchP);
		//transfert diffBar
		const diffP = (fromWedMgr.wedEditor as IWedEditorDiffBar).diffBar?.getDiffParams();
		if (diffP) editor.getDiffBar().then((bar) => {bar.setDiffParams(diffP)});
		editor.wedMgr.listeners.on("hidden", (wedMgr, closed) => {
			if (closed) wedMgr.killEditor();
		});
		return editor;
	}
}
